/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { getCatalogId, getUrlSync } from '@msdyn365-commerce/core';
import { ProductPrice, ProjectionDomain, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    isMobile,
    ITelemetryContent,
    KeyCodes,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import * as React from 'react';

import { FormComponent, LabelComponent } from './components';
import { IMilSearchData } from './mil-search.data';
import { IMilSearchProps, ISuggestionTypeCriterionData, SuggestionTypeCriterionSuggestionType } from './mil-search.props.autogenerated';
import { getMilSearchSuggestionsAsync } from '../../actions/DataActionExtension.g';
import { ISearchSuggestionsHitAttributesEntity, IUnitOfMeasureConversionResponseMapEntity } from '../../actions/DataServiceEntities.g';
import MilSearchSuggestionsItems from './components/mil-search-suggestions-items.component';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { MilSearchModal, MilSearchSuggestionModal } from './components/mil-search-modal.component';

export interface ISearchState {
    isSearchFormExpanded: boolean;
    searchText: string;
    autoSuggestAriaLabel: string;
    isInitialState: boolean;
    isLoading: boolean;
    hasError: boolean;
    products: SimpleProduct[];
    uomData: IUnitOfMeasureConversionResponseMapEntity[];
    luigisResp: ICustomSearchSuggestionHitsEntity[];
    modalIsOpen: boolean;
    viewport: GridSize;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface ICustomSearchSuggestionHitsEntity {
    Url?: number;
    Attributes?: ISearchSuggestionsHitAttributesEntity;
}

export interface ISearchSuggestionModalViewProps {
    modal: React.ReactElement;
    modalHeader: React.ReactElement;
    modalFooter: React.ReactElement;
    modalBody: React.ReactElement;
}

export interface ISearchViewProps extends IMilSearchProps<IMilSearchData> {
    Search: IModuleProps;
    AutoSuggestAriaLabel: INodeProps;
    AutoSuggestAriaLabelText: string;
    searchText: string;
    AutoSuggest: INodeProps;
    SearchForm: INodeProps;
    FormWrapper: INodeProps;
    UlKeyword: INodeProps;
    UlProduct: INodeProps;
    UlCategory: INodeProps;
    label: React.ReactNode;
    form: React.ReactNode;
    searchSuggestionsProducts: React.ReactNode | undefined;
    callbacks: {
        handleCancelSearchChange(): void;
        handleCancelSearchFocused?(): void;
    };
    isSearchFormExpanded: boolean;
    searchForText: string;
    renderError: JSX.Element | null;
    productsCount: number;
    isLoading?: boolean;
    isLoadingNode?: React.ReactNode;
    modalToggle: React.ReactNode;
    isMobile: boolean;
    milSearchSuggestionModal: ISearchSuggestionModalViewProps;
}

/**
 *
 * Search module.
 * @extends {React.Component<IMilSearchProps<IMilSearchData>, ISearchState>}
 */
class Search extends React.Component<IMilSearchProps<IMilSearchData>, ISearchState> {
    private readonly searchTextInput: React.RefObject<HTMLInputElement>;

    private readonly searchCollapsedLabel: React.RefObject<HTMLButtonElement>;

    private currentRequestToken = 0;

    private readonly formReference: React.RefObject<HTMLButtonElement>;

    private readonly maxChars: number;

    private readonly topResultsCount?: number;

    private readonly waitTime: number = 500;

    private readonly autoSuggestResultScreenReaderDiv: React.RefObject<HTMLDivElement>;

    private readonly autoSuggestResultDiv: React.RefObject<HTMLDivElement>;

    private readonly searchFormClass: string;

    private readonly searchLabelClass: string;

    private readonly telemetryContent: ITelemetryContent;

    private readonly _modalToggleRef: React.RefObject<HTMLButtonElement>;

    private inputSuggestionType: ISuggestionTypeCriterionData[];

    // @ts-expect-error
    private cancellabelDebounce: debounce;

    public constructor(props: IMilSearchProps<IMilSearchData>) {
        super(props);
        this.searchTextInput = React.createRef();
        this.searchCollapsedLabel = React.createRef();
        this.autoSuggestResultScreenReaderDiv = React.createRef();
        this.autoSuggestResultDiv = React.createRef();
        this.formReference = React.createRef();
        this.state = {
            isSearchFormExpanded: false,
            searchText: '',
            autoSuggestAriaLabel: '',
            isInitialState: true,
            isLoading: false,
            hasError: false,
            products: [],
            uomData: [],
            luigisResp: [],
            modalIsOpen: false,
            viewport: this._getViewport()
        };
        this.maxChars =
            (this.props.context &&
                this.props.context.app &&
                this.props.context.app.config &&
                this.props.context.app.config.searchInputMaxLength) ||
            50;

        this.inputSuggestionType = [];
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._updateViewport = this._updateViewport.bind(this);
        this._toggleModal = this._toggleModal.bind(this);
        this.topResultsCount = this.props.config && this.props.config.topResultsCount ? this.props.config.topResultsCount : 5;
        this.searchFormClass = 'ms-search__form';
        this.searchLabelClass = 'ms-search__label';
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );

        this._initSuggestionTypes();
    }

    public async componentDidMount(): Promise<void> {
        document.body && document.body.addEventListener('mousedown', this._focusOutTarget);
        window && window.addEventListener('keyup', this._keyup);
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._focusOutTarget, false);
        window && window.removeEventListener('keyup', this._keyup, false);
    }

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    private _toggleModal = (): void => {
        const isMobileViewport = this.state.viewport === 'xs' || this.state.viewport === 'sm';

        if (!this.state.modalIsOpen && !isMobileViewport) {
            return;
        }

        this.setState(prev => ({
            modalIsOpen: !prev.modalIsOpen
        }));
    };

    private _updateViewport(): void {
        const newViewport = this._getViewport();

        if (this.state.viewport !== newViewport) {
            this.setState({ viewport: newViewport }, () => {
                if (this.state.modalIsOpen && !(this.state.viewport === 'xs' || this.state.viewport === 'sm')) {
                    this._toggleModal();
                }
            });
        } else {
            if (this.state.modalIsOpen && !(newViewport === 'xs' || newViewport === 'sm')) {
                this._toggleModal();
            }
        }
    }

    public componentDidUpdate(): void {
        const {
            config: { hideSearchLabel }
        } = this.props;
        const { isSearchFormExpanded, isInitialState } = this.state;

        if (!this.props.context.request.params.isEditor) {
            if (!hideSearchLabel && isSearchFormExpanded) {
                this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
            } else if (!isInitialState) {
                this.searchCollapsedLabel && this.searchCollapsedLabel.current && this.searchCollapsedLabel.current.focus();
            }
        }
    }

    public shouldComponentUpdate(nextProps: IMilSearchProps<IMilSearchData>, nextState: ISearchState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    // eslint-disable-next-line complexity -- ignore complexity.
    public render(): JSX.Element {
        const {
            context,
            resources: { searchtext, searchLabelArialLabel, cancelBtnAriaLabel, searchBtnAriaLabel, submitBtnAriaLabel, modalTitle },
            config: { hideSearchLabel, disableSubmitSearch, searchplaceholderText, className = '' }
        } = this.props;

        const { isSearchFormExpanded, searchText, isLoading, products, uomData, luigisResp, modalIsOpen } = this.state;

        const searchQsp = (context && context.app && context.app.config && context.app.config.searchQueryStringParameter) || 'q';
        const searchPageURL = getUrlSync('search', context && context.actionContext) || '';
        const searchURL = this._appendQueryParams(searchPageURL, searchText, searchQsp);
        const searchPlaceholder = searchplaceholderText && searchplaceholderText.length > 0 ? searchplaceholderText : 'Search in Fabrikam';
        const isMobile = this.state.viewport === 'xs' || this.state.viewport === 'sm';

        const viewProps: ISearchViewProps = {
            ...(this.props as IMilSearchProps<IMilSearchData>),

            Search: {
                moduleProps: this.props,
                className: `${classnames('ms-search', className)} ${disableSubmitSearch ? 'no-submit' : 'with-submit'}`
            },
            AutoSuggestAriaLabel: {
                tag: 'div',
                className: 'msc-autoSuggest__screen-reader',
                'aria-live': 'assertive',
                ref: this.autoSuggestResultScreenReaderDiv
            },
            AutoSuggestAriaLabelText: this.state.autoSuggestAriaLabel,
            searchText: this.state.searchText,
            AutoSuggest: {
                ref: this.autoSuggestResultDiv,
                className: `ms-search__autoSuggest ${this.state.searchText && this.state.searchText.length > 0 ? 'show' : ''}`
            },
            SearchForm: {
                className: `${this.searchFormClass} ${isSearchFormExpanded || (isMobile && modalIsOpen) ? 'bx-show' : 'bx-hide'}`
            },
            FormWrapper: {
                tag: 'form',
                ref: this.formReference,
                className: 'ms-search__searchForm',
                'aria-label': 'Search',
                name: 'searchForm',
                role: 'search',
                action: searchURL,
                autoComplete: 'off',
                onSubmit: this._handleSubmit
            },
            UlKeyword: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__keywordResults-items', className)
            },
            UlProduct: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__productResults-items', className)
            },
            UlCategory: {
                tag: 'ul',
                className: classnames('msc-autoSuggest__categoryResults-items', className)
            },
            label: LabelComponent({
                isSearchFormExpanded,
                searchLabelClass: `${this.searchLabelClass} ${isSearchFormExpanded ? 'bx-hide' : 'bx-show'}`,
                searchtext,
                searchLabelArialLabel,
                searchCollapsedLabel: this.searchCollapsedLabel,
                hideSearchLabel,
                handleCancelSearchChange: isMobile ? this._toggleModal : this._handleCancelSearchChange,
                telemetryContent: this.telemetryContent
            }),
            searchForText: this.props.resources.searchForText,
            productsCount: this.state.products.length,
            renderError: this.renderNoProductsMessage(),
            form: FormComponent({
                ariaLabelCancel: cancelBtnAriaLabel,
                ariaLabelSearch: searchBtnAriaLabel,
                ariaLabelSubmit: submitBtnAriaLabel,
                hideSearchLabel,
                searchTextInput: this.searchTextInput,
                disableSubmitSearch,
                handleInputChange: this._handleInputChange,
                handleCancelSearchChange: this._handleCancelSearchChange,
                maxChars: this.maxChars,
                searchQsp,
                searchPlaceholder,
                query: searchText
            }),
            searchSuggestionsProducts:
                searchText && searchText.length > 0 && (isLoading || (products && ArrayExtensions.hasElements(products))) ? (
                    <MilSearchSuggestionsItems
                        context={context}
                        id={this.props.id}
                        resources={this.props.resources}
                        typeName={this.props.typeName}
                        uomData={uomData}
                        imageSettings={this.props.config.imageSettings}
                        products={products}
                        luigisResp={luigisResp}
                    />
                ) : (
                    undefined
                ),
            callbacks: {
                handleCancelSearchChange: this._handleCancelSearchChange,
                handleCancelSearchFocused: this._handleCancelSearchFocused
            },
            modalToggle: (
                <MilSearchModal
                    ariaLabel={modalTitle}
                    innerRef={this._modalToggleRef}
                    onClick={this._toggleModal}
                    id='search-result-modal'
                />
            ),
            isSearchFormExpanded: this.state.isSearchFormExpanded,
            isLoading,
            isLoadingNode: this._isLoadingNode(),
            isMobile: this.state.viewport === 'xs' || this.state.viewport === 'sm',
            milSearchSuggestionModal: this._getSearchResultModal()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private renderNoProductsMessage() {
        const { tryDiffKeywordSuggestionText, noResultsText } = this.props.resources;
        if (this.state.hasError && this.state.products.length === 0) {
            return (
                <div className='no-products-container'>
                    <p className='no-products-container__heading'>{tryDiffKeywordSuggestionText}</p>
                    <p className='no-products-container__text'>{noResultsText}</p>
                </div>
            );
        }

        return null;
    }

    private readonly _getSearchResultModal = (): ISearchSuggestionModalViewProps => {
        const { resources } = this.props;
        return MilSearchSuggestionModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            returnRef: this._modalToggleRef,
            onModalToggle: this._toggleModal
        });
    };

    private async loadSearchResults(searchTerm: string) {
        const myToken = ++this.currentRequestToken;
        const pageSize = this.topResultsCount;

        this.setState({ hasError: false, products: [], uomData: [], luigisResp: [] });

        try {
            const resp = await getMilSearchSuggestionsAsync(
                { callerContext: this.props.context.actionContext },
                searchTerm,
                pageSize || 10
            );

            if (myToken !== this.currentRequestToken) {
                return;
            }

            const results = resp?.Data?.Hits;
            const uomData = resp?.UomConversionData?.UomConvMap;
            if (!results || !uomData?.length || resp?.ExactMatchHitsCount === 0) {
                this.setState({ isLoading: false, hasError: true });
                return;
            }

            const catalogId = getCatalogId(this.props.context.actionContext.requestContext);

            const projectDomain: ProjectionDomain = {
                ChannelId: +this.props.context.actionContext.requestContext.apiSettings.channelId,
                CatalogId: catalogId
            };

            const recIds = uomData?.map(uom => uom.ItemRecId);
            const distinctIds = Array.from(new Set(recIds));

            const productPrices: ProductPrice[] = await getActivePricesAsync(
                { callerContext: this.props.context.actionContext },
                projectDomain,
                distinctIds,
                new Date(),
                null,
                undefined,
                true
            );

            const allProducts: SimpleProduct[] | undefined = uomData.map(uom => uom.Product!);
            const priceZero = 0;

            allProducts.forEach(product => {
                const activePrice = productPrices?.find(price => price.ProductId === product.RecordId);
                if (activePrice) {
                    product.Price = activePrice.CustomerContextualPrice ?? product.Price;
                    product.BasePrice = Math.max(
                        activePrice.BasePrice ?? priceZero,
                        activePrice.TradeAgreementPrice ?? priceZero,
                        activePrice.AdjustedPrice ?? priceZero
                    );
                }
            });

            const normalizedRecidsResp = results.map(prod => ({
                ...prod,
                Url: Number(prod.Url?.replace(/\.p$/, ''))
            }));

            this.setState(prev => {
                return {
                    isLoading: false,
                    hasError: false,
                    products: allProducts,
                    uomData,
                    luigisResp: normalizedRecidsResp
                };
            });
        } catch (err) {
            console.error('Failed to load Luigi search results', err);
            if (myToken === this.currentRequestToken) {
                this.setState({ isLoading: false, hasError: true });
            }
        }
    }

    private _initSuggestionTypes() {
        const suggestionTypes = this.props.config.suggestionTypeCriterion;

        const suggestions = ArrayExtensions.unique(ArrayExtensions.validValues(suggestionTypes));
        if (!ArrayExtensions.hasElements(suggestions)) {
            this.inputSuggestionType = [
                { SuggestionType: SuggestionTypeCriterionSuggestionType.keyword },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.product },
                { SuggestionType: SuggestionTypeCriterionSuggestionType.scopedCategory }
            ];
            return;
        }

        const validSuggestions = suggestions.filter(suggestion => suggestion.SuggestionType);
        if (ArrayExtensions.hasElements(validSuggestions)) {
            this.inputSuggestionType = validSuggestions;
            return;
        }

        this.inputSuggestionType = [];
    }

    /**
     * Function to create the ReactNode for loading Autosuggest result.
     * @returns ReachNode for loading Autosuggest result.
     */
    private readonly _isLoadingNode = (): React.ReactNode => {
        const loadingText = this.props.resources.autoSuggestResultLoadingMessage;
        return <Spinner className='msc-autoSuggest__loadingResult' label={loadingText} size={SpinnerSize.medium} labelPosition='right' />;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _focusOutTarget = (e: any): void => {
        const { products } = this.state;

        if (products && products.length && e.target.closest('.ms-search') === null) {
            this._clearAutoSuggestState();
        }
    };

    private readonly _keyup = (e: KeyboardEvent): void => {
        const { products } = this.state;

        if (products && products.length && e.keyCode === KeyCodes.Escape) {
            this._clearAutoSuggestState();
        } else if (this.state.isSearchFormExpanded && e.keyCode === KeyCodes.Escape) {
            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }
        }

        if (
            !(
                (this.formReference.current && this.formReference.current.contains((e.target as unknown) as Node)) ||
                (this.autoSuggestResultDiv.current && this.autoSuggestResultDiv.current.contains((e.target as unknown) as Node))
            )
        ) {
            if (products && products.length) {
                this._clearAutoSuggestState();
            }

            if (this.state.isSearchFormExpanded) {
                this.setState({ isSearchFormExpanded: false });
            }
        }
    };

    private readonly _clearAutoSuggestState = (): void => {
        // if (this.searchTextInput.current && this.searchTextInput.current.value.length > 0) {
        //     searchState.searchText = '';
        //     searchState.defaultBasicSearchUrl = '';
        //     searchState.searchQueryStringParameter = '';
        //     actionContext ? actionContext.update(createSearchStateInput(searchState), searchState) : null;
        //     this.searchTextInput.current.value = '';
        // }

        this.setState({ searchText: '', products: [], uomData: [] });
    };

    private _loadSuggestions(query: string) {
        this.cancellabelDebounce = debounce(async () => {
            this.setState({ isLoading: true });
            await this.loadSearchResults(query);
        }, this.waitTime);

        setTimeout(() => {
            this.cancellabelDebounce();
        }, 0);
    }

    private readonly _handleInputChange = (query: string): void => {
        if (!query || query.length <= this.maxChars) {
            this.setState({ searchText: query });
        }

        if (this.cancellabelDebounce) {
            this.cancellabelDebounce.cancel();
        }

        const {
            context
            // data: { searchState }
        } = this.props;

        if (context) {
            if (query && query.length > 0) {
                if (query.length > this.maxChars) {
                    return;
                }

                if (ArrayExtensions.hasElements(this.inputSuggestionType)) {
                    this._loadSuggestions(query);
                }

                // searchState.searchText = query;
            } else {
                this.setState({ products: [] });
                // searchState.searchText = '';
            }
            // context.actionContext.update();
        }
    };

    private readonly _handleSubmit = (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>
    ): void => {
        const {
            config: { disableSubmitSearch },
            context: {
                actionContext,
                app: {
                    config: { searchQueryStringParameter }
                }
            }
        } = this.props;

        const query = this.searchTextInput.current && this.searchTextInput.current.value;
        e.preventDefault();

        if (!query || (query && query.length === 0) || disableSubmitSearch) {
            this.searchTextInput && this.searchTextInput.current && this.searchTextInput.current.focus();
        } else {
            const searchQsp = searchQueryStringParameter || 'q';
            const searchURL = this._appendQueryParams(getUrlSync('search', actionContext) || '', query, searchQsp);
            document.location.href = searchURL;
        }
    };

    private readonly _handleCancelSearchChange = (): void => {
        const { isSearchFormExpanded, products } = this.state;

        if (!isSearchFormExpanded) {
            this.setState({
                isSearchFormExpanded: true,
                searchText: '',
                isInitialState: false
            });
        } else {
            if (products && products.length) {
                this._clearAutoSuggestState();
            }
            this.setState({ isSearchFormExpanded: false });
        }
    };

    private readonly _handleCancelSearchFocused = (): void => {
        const { products } = this.state;
        if (!(products && products.length)) {
            this._clearAutoSuggestState();
            this.setState({ isSearchFormExpanded: false });
        }
    };

    private readonly _appendQueryParams = (route: string, query: string, qsp: string): string => {
        if (!MsDyn365.isBrowser) {
            return '';
        }

        const queryUrl = new URL(route, window.location.href);
        if (qsp && query) {
            queryUrl.searchParams.set(qsp, query);
        }

        return queryUrl.href;
    };
}

export default Search;
